"use client";

import * as React from "react";
import {
  HandCoins,
  LayoutGrid,
  Settings,
  ShoppingBag,
  CreditCard,
  BarChart3,
  HelpCircle,
  Calendar,
  Users,
  Bell,
  Bot,
  PiggyBank,
  FileText,
  Share2,
  ClipboardCheck,
  History,
  Landmark,
  ReceiptText,
  Search,
  X,
} from "lucide-react";

import { NavMain } from "@/components/ui/nav-main";
import { NavUser } from "@/components/ui/nav-user";
import { PortalSwitcher } from "@/components/ui/portal-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useAuth } from "@/context/auth.context";
import { can } from "@/hooks/usePermissions";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

import "./app-sidebar-brand.css";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, portalMode } = useAuth();
  const pathname = useLocation().pathname;

  const getDisplayName = React.useCallback(() => {
    const rawName = (user?.fullName || user?.name || "").trim();
    const composedName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    const emailPrefix = user?.email ? String(user.email).split("@")[0] : "";
    const placeholderNames = ["default user", "member user", "user", "n/a"];
    const knownRoleSuffixes = [
      "Technical Support",
      "President",
      "Secretary",
      "Accountant",
      "Admin",
      "Member",
    ];

    const cleanRoleSuffix = (value: string) => {
      let cleaned = value.trim();
      for (const suffix of knownRoleSuffixes) {
        const regex = new RegExp(`\\s+${suffix}$`, "i");
        if (regex.test(cleaned)) {
          cleaned = cleaned.replace(regex, "").trim();
        }
      }
      return cleaned;
    };

    const candidates = [rawName, composedName, user?.firstName || "", emailPrefix];
    for (const candidate of candidates) {
      const cleaned = cleanRoleSuffix(candidate || "");
      if (!cleaned) continue;
      if (placeholderNames.includes(cleaned.toLowerCase())) continue;
      return cleaned;
    }

    return "Member User";
  }, [user]);

  const data = React.useMemo(() => {
    return {
      user: {
        name: getDisplayName(),
        email: user?.email || "member@group.rw",
        profileUrl: user?.profileUrl,
        role: (() => {
          if (portalMode === "admin") {
            const leadershipRole = user?.leadershipRole || user?.role?.name || (typeof user?.role === 'string' ? user.role : null);
            if (leadershipRole && ["Technical Support", "President", "Secretary", "Accountant"].includes(leadershipRole)) {
              return leadershipRole;
            }
            if (user?.isAdmin) return "Case d'Entraide";
            return leadershipRole || "Member";
          }
          return "Member";
        })(),
        isAdmin: portalMode === "admin",
        leadershipRole: portalMode === "admin" ? (user?.leadershipRole || user?.role?.name || (typeof user?.role === 'string' && ["Technical Support", "President", "Secretary", "Accountant"].includes(user.role) ? user.role : null)) : null,
        group: user?.group,
        memberCode: user?.memberCode || user?.teacherId,
        avatar: user?.firstName ? user.firstName.charAt(0) + (user.lastName?.charAt(0) || '') : 'M',
      },
      navMain: portalMode === "admin"
        ? [
        {
          title: "Dashboard",
          url: "/dashboard",
          icon: LayoutGrid,
          isActive: pathname.startsWith("/dashboard"),
          permission: "dashboard.view",
        },
        {
          title: "Members",
          url: "/members",
          icon: Users,
          isActive: pathname.startsWith("/members"),
          permission: "members.view",
        },
        {
          title: "Contributions",
          url: "/contributions",
          icon: HandCoins,
          isActive: pathname.startsWith("/contributions") || pathname.startsWith("/shares"),
          permission: "contributions.view",
        },
        {
          title: "Savings",
          url: "/savings",
          icon: PiggyBank,
          isActive: pathname.startsWith("/savings"),
          permission: "contributions.view",
        },
        {
          title: "Loans",
          url: "/loans",
          icon: CreditCard,
          isActive: pathname === "/loans" || (pathname.startsWith("/loans/") && !pathname.startsWith("/loans/payments")),
          permission: "loans.view",
        },
        {
          title: "Loan Repayments",
          url: "/loan-repayments",
          icon: ReceiptText,
          isActive: pathname.startsWith("/loan-repayments") || pathname.startsWith("/loans/payments"),
          permission: "loans.view",
        },
        {
          title: "Interest Distribution",
          url: "/interest-distribution",
          icon: BarChart3,
          isActive: pathname.startsWith("/interest-distribution"),
          permission: "loans.view",
        },
        {
          title: "Approvals Center",
          url: "/approvals",
          icon: ClipboardCheck,
          isActive: pathname.startsWith("/approvals") || pathname.startsWith("/member-payments"),
          permission: "loans.approve",
        },
        {
          title: "Expenses",
          url: "/expenses",
          icon: ShoppingBag,
          isActive: pathname.startsWith("/expenses"),
          permission: "expenses.view",
        },
        {
          title: "Finance",
          url: "/finance/accounts",
          icon: Landmark,
          isActive: pathname.startsWith("/finance"),
          permission: "settings.view",
          items: [
            { title: "Accounts", url: "/finance/accounts" },
            { title: "Journal Entries", url: "/finance/journals" },
            { title: "Chart of Accounts", url: "/finance/chart-of-accounts" },
            { title: "Ledger", url: "/finance/ledger" },
          ],
        },
        {
          title: "Meetings & Attendance",
          url: "#",
          icon: Calendar,
          isActive: pathname.startsWith("/meetings") || pathname.startsWith("/attendance"),
          permission: "meetings.view",
          items: [
            { title: "Meetings", url: "/meetings" },
            { title: "Attendance", url: "/attendance" },
          ],
        },
        {
          title: "Reports & Analytics",
          url: "/reports/financial",
          icon: BarChart3,
          isActive: pathname.startsWith("/reports") && !pathname.includes("/statement"),
          permission: "reports.view",
          items: [
            { title: "Financial Reports", url: "/reports/financial" },
            { title: "Member Reports", url: "/reports/members" },
            { title: "Savings Reports", url: "/reports/savings" },
            { title: "Loan Reports", url: "/reports/loans" },
            { title: "Repayment Reports", url: "/reports/repayments" },
            { title: "Payroll Deduction Reports", url: "/reports/payroll" },
            { title: "Approval Reports", url: "/reports/approvals" },
          ],
        },
        {
          title: "Statements",
          url: "/statements",
          icon: FileText,
          isActive: pathname.startsWith("/statements") || pathname.includes("/statement"),
          permission: "reports.view",
        },
      ]
      : [
        {
          title: "My Dashboard",
          url: "/member/dashboard",
          icon: LayoutGrid,
          isActive: pathname.startsWith("/member/dashboard"),
        },
        {
          title: "My Contribution",
          url: "/member/contributions",
          icon: HandCoins,
          isActive: pathname === "/member/contributions",
        },
        {
          title: "My Savings",
          url: "/member/savings/record",
          icon: PiggyBank,
          isActive: pathname.startsWith("/member/savings/record"),
        },
        {
          title: "My Shares",
          url: "/member/shares",
          icon: Share2,
          isActive: pathname.startsWith("/member/shares"),
        },
        {
          title: "My Loans",
          url: "/member/loans",
          icon: CreditCard,
          isActive: pathname.startsWith("/member/loans"),
        },
        {
          title: "Reports",
          url: "/member/reports/dashboard",
          icon: BarChart3,
          isActive: pathname.startsWith("/member/reports") && pathname !== "/member/reports/statement",
        },
        {
          title: "Statements",
          url: "/member/reports/statement",
          icon: FileText,
          isActive: pathname === "/member/reports/statement",
        },
        {
          title: "Meetings",
          url: "/member/meetings",
          icon: Calendar,
          isActive: pathname.startsWith("/member/meetings"),
        },
        {
          title: "Notifications",
          url: "/member/notifications",
          icon: Bell,
          isActive: pathname.startsWith("/member/notifications"),
        },
        {
          title: "Support Center",
          url: "/member/support",
          icon: HelpCircle,
          isActive: pathname.startsWith("/member/support"),
        },
        {
          title: "My Profile",
          url: "/member/profile",
          icon: Users,
          isActive: pathname.startsWith("/member/profile"),
        },
        {
          title: "Settings",
          url: "/member/settings",
          icon: Settings,
          isActive: pathname.startsWith("/member/settings"),
        },
      ],
      projects: portalMode === "admin"
        ? [
        {
            title: "System Settings",
            url: "/settings",
            icon: Settings,
            isActive: pathname.startsWith("/settings"),
            permission: "settings.manage",
        },
        {
            title: "Approval Workflows",
            url: "/settings/approval-workflows",
            icon: ClipboardCheck,
            isActive: pathname.startsWith("/settings/approval-workflows"),
            permission: "settings.manage",
        },
        {
          title: "Audit Logs",
          url: "/audit-logs",
          icon: History,
          isActive: pathname.startsWith("/audit-logs"),
          permission: "settings.manage",
        },
        {
          title: "HR Payroll",
          url: "/payroll",
          icon: Settings,
          isActive: pathname.startsWith("/payroll"),
          permission: "payroll.view",
        },
        {
          title: "AI Assistant",
          url: "/ai-assistant",
          icon: Bot,
          isActive: pathname.startsWith("/ai-assistant"),
          permission: "ai.view",
        },
        {
          title: "Member Questions",
          url: "/support-tickets",
          icon: HelpCircle,
          isActive: pathname.startsWith("/support-tickets"),
          permission: "settings.view",
        },
        {
          title: "Help & Support",
          url: "/help/support",
          icon: HelpCircle,
          isActive: pathname.startsWith("/help"),
        },
      ]
      : [],
    };
  }, [user, pathname, getDisplayName, portalMode]);

  const isAdminView = portalMode === "admin";

  const filterByPermission = React.useCallback((items: any[]) =>
    items.filter(item => !item.permission || can(user, item.permission)),
  [user]);

  const filteredNavMain = filterByPermission(data.navMain);
  const filteredProjects = filterByPermission(data.projects);

  // ─── Sidebar Search ───
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const allNavItems = React.useMemo(() => {
    const items: { title: string; url: string; icon?: any; section: string }[] = [];
    for (const item of filteredNavMain) {
      items.push({ title: item.title, url: item.url, icon: item.icon, section: "nav" });
      if (item.items) {
        for (const sub of item.items) {
          items.push({ title: sub.title, url: sub.url, section: item.title });
        }
      }
    }
    for (const item of filteredProjects) {
      items.push({ title: item.title, url: item.url, icon: item.icon, section: "admin" });
    }
    return items;
  }, [filteredNavMain, filteredProjects]);

  const searchResults = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    return allNavItems.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.section.toLowerCase().includes(q)
    );
  }, [searchQuery, allNavItems]);

  const isSearching = searchQuery.trim().length > 0;

  // Keyboard shortcut: Ctrl+K or Cmd+K to focus search
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === "Escape" && isSearching) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isSearching]);

  const overviewNav = filteredNavMain[0] ? [filteredNavMain[0]] : [];
  const coreNav = filteredNavMain.length > 1 ? filteredNavMain.slice(1) : [];

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <PortalSwitcher />
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search... ⌘K"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "flex h-9 w-full rounded-lg border border-input bg-background pl-9 pr-8 py-2 text-sm text-foreground shadow-sm transition-colors",
              "placeholder:text-muted-foreground/60",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4dbd07]/30 focus-visible:border-[#4dbd07]",
              "group-data-[collapsible=icon]:hidden"
            )}
          />
          {/* Collapsed icon-mode button */}
          <button
            onClick={() => searchInputRef.current?.focus()}
            className={cn(
              "hidden group-data-[collapsible=icon]:flex items-center justify-center size-8 rounded-lg border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors mx-auto",
            )}
            title="Search (⌘K)"
          >
            <Search className="size-4" />
          </button>
          {isSearching && (
            <button
              onClick={() => {
                setSearchQuery("");
                searchInputRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent className="gap-0 pb-4">
        {isSearching ? (
          searchResults.length > 0 ? (
            <NavMain
              title=""
              items={searchResults.map((r) => ({
                title: r.title,
                url: r.url,
                icon: r.icon,
                isActive: pathname === r.url,
              }))}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <Search className="size-8 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No results found</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Try a different search term
              </p>
            </div>
          )
        ) : (
          <>
            {overviewNav.length > 0 ? (
              isAdminView ? (
                <NavMain title="Dashboard" items={overviewNav} />
              ) : (
                <NavMain title="Overview" items={overviewNav} />
              )
            ) : null}
            {coreNav.length > 0 ? (
              <NavMain title="Core Management" items={coreNav} />
            ) : null}
            {filteredProjects.length > 0 ? (
              <NavMain title="Administration" items={filteredProjects} />
            ) : null}
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
